import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronRight, ArrowDown } from "lucide-react";
import { IMAGES, LOGOS } from "@/data/site";

export const KineticHero = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.6, 0.92]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative h-screen w-full overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroMotion} alt="Karting Bahamas racing" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/40 to-black/40" />
      <div className="grain-overlay" />

      {/* Centered content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 mx-auto flex h-full max-w-[1100px] flex-col items-center justify-center px-6 text-center"
      >
        {/* Logo front & centered */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex items-center justify-center"
        >
          <div className="pointer-events-none absolute -inset-x-16 -inset-y-10 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.9),rgba(255,255,255,0.35)_45%,transparent_72%)] blur-2xl" />
          <img
            src={LOGOS.full}
            alt="Karting Bahamas"
            className="relative w-full max-w-[320px] select-none sm:max-w-[420px] md:max-w-[540px] lg:max-w-[620px]"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mt-10 max-w-xl text-base leading-relaxed text-white/75 md:text-lg"
        >
          Outdoor go-kart racing built on the Southern Thomas A. Robinson Stadium parking lot —
          2 tyre-lined circuits. Just arrive, sign, gear up and drive!
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05 }}
          className="mt-9 flex flex-wrap justify-center gap-4"
        >
          <button
            onClick={() => navigate("/experience")}
            data-testid="hero-experience-btn"
            className="group flex items-center gap-2 overflow-hidden rounded-xl bg-kbcyan px-7 py-4 font-display text-2xl tracking-wide text-black transition-transform hover:scale-[1.03]"
          >
            Explore The Track
            <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => navigate("/pricing")}
            data-testid="hero-pricing-btn"
            className="rounded-xl border border-white/25 bg-white/5 px-7 py-4 font-display text-2xl tracking-wide text-white backdrop-blur-sm transition-colors hover:border-kbyellow hover:text-kbyellow"
          >
            View Pricing
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 items-center gap-2 text-white/50 md:flex"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
        <span className="font-display tracking-[0.3em]">SCROLL</span>
      </motion.div>
    </section>
  );
};

export default KineticHero;
